import { StoreApi } from 'zustand';

import { useLivePriceWebSocketStore } from '~/stores/use-live-price-websocket-store/useLivePriceWebSocketStore';
import { create } from '~/utils/store/createStore';

import {
  Identifier,
  LivePriceData,
  LivePriceState,
  mapNewLivePriceDataToWatchedData,
  mapPriceEntityToLivePriceData,
  PriceEntity,
  priceUpdater,
  SubscribeConfig,
  updateWatching,
} from './useLivePriceStore.utils';
import { TickerPayload } from '~/services/price-ws/types';

const unsubscribe =
  ({
    tickers,
    priceEntities,
    get,
    set,
    identifier,
  }: {
    tickers: TickerPayload[];
    priceEntities: PriceEntity[];
    get: StoreApi<LivePriceState>['getState'];
    set: StoreApi<LivePriceState>['setState'];
    identifier: Identifier;
  }) =>
  () => {
    const { unsubscribe: unsubscribeWS } =
      useLivePriceWebSocketStore.getState();

    if (!tickers.length) {
      return;
    }

    priceEntities.forEach(({ entity }, index) => {
      const isDeleted = updateWatching(
        set,
        get,
        identifier,
        entity.subscribable_ticker
      );

      if (isDeleted) {
        unsubscribeWS([tickers[index]]);
      }
    });

    const withoutTickers = Object.keys(
      get().componentsWatchingData
    ).reduce<LivePriceData>((livePriceData, key) => {
      return { ...livePriceData, [key]: get().livePriceData[key] };
    }, {});

    set({ livePriceData: withoutTickers });
  };

export const useLivePriceStore = create<LivePriceState>((set, get) => ({
  livePriceData: {},
  componentsWatchingData: {},
  subscribe: ({
    priceEntities,
    identifier = Identifier.Global,
    delayedPrices,
  }: SubscribeConfig) => {
    const { livePriceData, componentsWatchingData } = get();

    const { data: newLivePriceData, tickers } = mapPriceEntityToLivePriceData(
      priceEntities,
      livePriceData
    );

    const watchingLivePriceData = mapNewLivePriceDataToWatchedData(
      priceEntities,
      newLivePriceData,
      identifier,
      componentsWatchingData
    );

    const { subscribe: subscribeWS } = useLivePriceWebSocketStore.getState();

    subscribeWS({ tickers, updateHandler: priceUpdater(set), delayedPrices });

    set({
      livePriceData: { ...livePriceData, ...newLivePriceData },
      componentsWatchingData: watchingLivePriceData,
    });

    return unsubscribe({ tickers, priceEntities, get, set, identifier });
  },
}));
